import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface EventData {
  id: string;
  title: string;
  slug: string;
  date: string;
  location: string;
}

interface EmailStatus {
  lastAttempt: {
    sentAt: string;
    success: boolean;
    errorMessage?: string;
  } | null;
  statistics: {
    totalAttempts: number;
    successCount: number;
    failureCount: number;
  };
}

const EmailSupportPage: React.FC = () => {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState<EventData | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  useEffect(() => {
    if (eventSlug) {
      fetchEventData();
    }
  }, [eventSlug]);

  // Buscar status do email quando o usuário digitar
  useEffect(() => {
    if (email.trim() && eventSlug) {
      const timeoutId = setTimeout(() => {
        fetchEmailStatus(email.trim());
      }, 1000); // Debounce de 1 segundo

      return () => clearTimeout(timeoutId);
    } else {
      setEmailStatus(null);
    }
  }, [email, eventSlug]);

  const fetchEventData = async () => {
    try {
      setLoadingEvent(true);
      const response = await fetch(`/api/suporte-email/${eventSlug}`);
      const data = await response.json();
      
      if (data.success) {
        setEventData(data.event);
      } else {
        setMessage({ type: 'error', text: 'Evento não encontrado' });
      }
    } catch (error) {
      console.error('Erro ao buscar dados do evento:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar dados do evento' });
    } finally {
      setLoadingEvent(false);
    }
  };

  const fetchEmailStatus = async (emailToCheck: string) => {
    if (!eventSlug || !emailToCheck.trim()) return;

    try {
      setLoadingStatus(true);
      const response = await fetch(`/api/suporte-email/email-status?email=${encodeURIComponent(emailToCheck)}&eventSlug=${eventSlug}`);
      const data = await response.json();
      
      if (data.success) {
        setEmailStatus(data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar status do email:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleResendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Por favor, digite seu email' });
      return;
    }

    if (!eventSlug) {
      setMessage({ type: 'error', text: 'Slug do evento não encontrado' });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const response = await fetch('/api/suporte-email/resend-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          eventSlug
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Navegar para página de sucesso com dados do evento e email
        navigate(`/${eventSlug}/email-enviado`, {
          state: {
            eventData: eventData,
            email: email.trim()
          }
        });
      } else {
        // Tratar rate limit especificamente
        if (response.status === 429) {
          setMessage({ type: 'error', text: 'Muitos emails enviados recentemente. Aguarde alguns minutos e tente novamente.' });
        } else {
          setMessage({ type: 'error', text: data.message || 'Erro ao reenviar email' });
        }
      }
    } catch (error) {
      console.error('Erro ao reenviar email:', error);
      setMessage({ type: 'error', text: 'Erro de conexão. Tente novamente.' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEvent = () => {
    if (eventSlug) {
      navigate(`/${eventSlug}`);
    } else {
      navigate('/');
    }
  };

  if (loadingEvent) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!eventData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Evento não encontrado. Verifique se o link está correto.
        </Alert>
        <Box mt={2}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Voltar ao início
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={3}>
        <IconButton 
          onClick={handleBackToEvent}
          sx={{ mb: 2 }}
          color="primary"
        >
          <ArrowBackIcon />
        </IconButton>
        
        <Typography variant="h4" component="h1" gutterBottom>
          <EmailIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Suporte - Reenvio de Email
        </Typography>
        
        <Typography variant="body1" color="text.secondary">
          Não recebeu o email de confirmação? Digite seu email abaixo para reenviarmos.
        </Typography>
      </Box>

      {/* Event Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {eventData.title}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {format(new Date(eventData.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </Typography>
          </Box>
          
          {eventData.location && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {eventData.location}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Support Form */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reenviar Email de Confirmação
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Digite o email que você usou para se inscrever no evento. 
          Enviaremos novamente o email de confirmação com seu ingresso.
        </Typography>

        {message && (
          <Alert 
            severity={message.type} 
            sx={{ mb: 3 }}
            icon={message.type === 'success' ? <CheckCircleIcon /> : undefined}
          >
            {message.text}
          </Alert>
        )}

        <form onSubmit={handleResendEmail}>
          <TextField
            fullWidth
            label="Seu email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="exemplo@email.com"
            disabled={loading}
            sx={{ mb: 2 }}
            required
          />

          {/* Status do Email */}
          {email.trim() && (
            <Box sx={{ mb: 3 }}>
              {loadingStatus ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Verificando histórico de envios...
                  </Typography>
                </Box>
              ) : emailStatus && emailStatus.lastAttempt ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Último envio:</strong> {format(new Date(emailStatus.lastAttempt.sentAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </Typography>
                  
                  {emailStatus.lastAttempt.success ? (
                    <Alert severity="success" sx={{ mb: 1 }}>
                      ✅ Email enviado com sucesso!
                    </Alert>
                  ) : (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      ❌ Falha no envio: {emailStatus.lastAttempt.errorMessage || 'Erro desconhecido'}
                    </Alert>
                  )}

                  {emailStatus.statistics.totalAttempts > 1 && (
                    <Typography variant="body2" color="text.secondary">
                      Total de tentativas: {emailStatus.statistics.totalAttempts} 
                      ({emailStatus.statistics.successCount} sucessos, {emailStatus.statistics.failureCount} falhas)
                    </Typography>
                  )}
                </Box>
              ) : emailStatus && emailStatus.statistics.totalAttempts === 0 ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  ℹ️ Nenhum envio registrado para este email ainda.
                </Alert>
              ) : null}
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleBackToEvent}
              disabled={loading}
            >
              Cancelar
            </Button>
            
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              disabled={loading || !email.trim()}
            >
              {loading ? 'Enviando...' : 'Reenviar Email'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Help Text */}
      <Box mt={3}>
        <Typography variant="body2" color="text.secondary" align="center">
          <strong>Dica:</strong> Verifique também sua pasta de spam/lixo eletrônico. 
          Às vezes os emails podem ser direcionados para lá.
        </Typography>
      </Box>
    </Container>
  );
};

export default EmailSupportPage;
